import { useEffect, useRef, useState } from 'react';
import {
    Anchor,
    Badge,
    Button,
    Divider,
    FileInput,
    Group,
    Loader,
    Paper,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { RichTextEditor, Link } from '@mantine/tiptap';
import { Editor, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { IconArrowBackUp, IconFileText, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useGetFacilityInvitationMessages } from '../../../global/entities/FacilityInvitationMessages/queries/useGetFacilityInvitationMessages.tsx';
import { useSaveFacilityInvitationMessages } from '../../../global/entities/FacilityInvitationMessages/mutations/useSaveFacilityInvitationMessages.tsx';
import { useResetFacilityInvitationMessage } from '../../../global/entities/FacilityInvitationMessages/mutations/useResetFacilityInvitationMessage.tsx';
import { useSendTestInvitation } from '../../../global/entities/FacilityInvitationMessages/mutations/useSendTestInvitation.tsx';
import { useGetFacilityInvitationPdfs } from '../../../global/entities/FacilityInvitationPdfs/queries/useGetFacilityInvitationPdfs.tsx';
import { useUploadFacilityInvitationPdf } from '../../../global/entities/FacilityInvitationPdfs/mutations/useUploadFacilityInvitationPdf.tsx';
import { useDeleteFacilityInvitationPdf } from '../../../global/entities/FacilityInvitationPdfs/mutations/useDeleteFacilityInvitationPdf.tsx';
import { IFacilityInvitationPdfFile } from '../../../global/entities/FacilityInvitationPdfs/FacilityInvitationPdfs.ts';

interface HcfInvitationMessagePanelProps {
    facilityId: number;
}

const MAX_PDF_SIZE = 5 * 1024 * 1024;
const MAX_INVITATION_PDFS = 5;

const useMessageEditor = () =>
    useEditor({
        extensions: [StarterKit, Link],
        content: '',
    });

function MessageEditor({ editor }: { editor: Editor | null }) {
    return (
        <RichTextEditor editor={editor}>
            <RichTextEditor.Toolbar sticky>
                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Strikethrough />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Link />
                    <RichTextEditor.Unlink />
                </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>
            <RichTextEditor.Content />
        </RichTextEditor>
    );
}

interface InvitationPdfSectionProps {
    facilityId: number;
    language: 'en' | 'fr';
    currentFiles: IFacilityInvitationPdfFile[];
}

function InvitationPdfSection({
    facilityId,
    language,
    currentFiles,
}: InvitationPdfSectionProps) {
    const { t } = useTranslation();
    const [files, setFiles] = useState<File[]>([]);
    const [error, setError] = useState<string | null>(null);
    const uploadMutation = useUploadFacilityInvitationPdf();
    const deleteMutation = useDeleteFacilityInvitationPdf();

    const remainingSlots = MAX_INVITATION_PDFS - currentFiles.length;
    const atCapacity = remainingSlots <= 0;

    const handleChange = (selected: File[]) => {
        setError(null);
        if (selected.some((f) => f.size > MAX_PDF_SIZE)) {
            setError(t('invitationPdf.admin.tooLarge'));
            return;
        }
        if (selected.length > remainingSlots) {
            setError(t('invitationPdf.admin.maxFiles', { count: MAX_INVITATION_PDFS }));
            return;
        }
        setFiles(selected);
    };

    const handleUpload = async () => {
        if (files.length === 0) return;
        for (const file of files) {
            try {
                await uploadMutation.mutateAsync({ facilityId, language, file });
            } catch {
                // The mutation surfaces its own error notification; keep going so
                // one bad file does not block the rest of the batch.
            }
        }
        setFiles([]);
    };

    return (
        <Stack gap="xs">
            <Text size="sm" c="dimmed">
                {t('invitationPdf.admin.sectionDescription')}
            </Text>
            {currentFiles.length > 0 ? (
                <Stack gap="xs">
                    <Text size="sm">{t('invitationPdf.admin.currentFiles')}:</Text>
                    {currentFiles.map((current) => (
                        <Group key={current.filename} gap="xs">
                            <IconFileText size={16} />
                            <Anchor
                                href={current.downloadUrl ?? '#'}
                                target="_blank"
                                size="sm"
                                style={{ flex: 1 }}
                            >
                                {current.filename}
                            </Anchor>
                            <Button
                                variant="subtle"
                                color="red"
                                size="compact-sm"
                                leftSection={<IconTrash size={14} />}
                                loading={deleteMutation.isPending}
                                onClick={() =>
                                    deleteMutation.mutate({
                                        facilityId,
                                        language,
                                        filename: current.filename,
                                    })
                                }
                            >
                                {t('invitationPdf.admin.remove')}
                            </Button>
                        </Group>
                    ))}
                </Stack>
            ) : (
                <Text size="sm" c="dimmed">
                    {t('invitationPdf.admin.none')}
                </Text>
            )}
            <Group align="flex-end">
                <FileInput
                    style={{ flex: 1 }}
                    label={t('invitationPdf.admin.fileLabel')}
                    placeholder={t('invitationPdf.admin.placeholder')}
                    accept="application/pdf"
                    multiple
                    leftSection={<IconFileText size={16} />}
                    clearable
                    value={files}
                    error={error}
                    disabled={atCapacity}
                    onChange={handleChange}
                />
                <Button
                    onClick={handleUpload}
                    disabled={files.length === 0}
                    loading={uploadMutation.isPending}
                >
                    {t('invitationPdf.admin.upload')}
                </Button>
            </Group>
            {atCapacity && (
                <Text size="xs" c="dimmed">
                    {t('invitationPdf.admin.maxFiles', { count: MAX_INVITATION_PDFS })}
                </Text>
            )}
        </Stack>
    );
}

interface MessageSectionHeaderProps {
    label: string;
    isCustom: boolean;
    onReset: () => void;
    resetting: boolean;
}

function MessageSectionHeader({
    label,
    isCustom,
    onReset,
    resetting,
}: MessageSectionHeaderProps) {
    const { t } = useTranslation();
    return (
        <Group justify="space-between" align="center">
            <Text fw={600}>{label}</Text>
            {isCustom ? (
                <Button
                    variant="subtle"
                    color="red"
                    size="compact-sm"
                    leftSection={<IconArrowBackUp size={14} />}
                    loading={resetting}
                    onClick={onReset}
                >
                    {t('invitationMessage.admin.reset')}
                </Button>
            ) : (
                <Badge variant="light" color="gray">
                    {t('invitationMessage.admin.usingDefault')}
                </Badge>
            )}
        </Group>
    );
}

export default function HcfInvitationMessagePanel({
    facilityId,
}: HcfInvitationMessagePanelProps) {
    const { t } = useTranslation();
    const { data, isLoading } = useGetFacilityInvitationMessages(facilityId);
    const { data: pdfs } = useGetFacilityInvitationPdfs(facilityId);
    const saveMutation = useSaveFacilityInvitationMessages();
    const resetMutation = useResetFacilityInvitationMessage();
    const testMutation = useSendTestInvitation();
    const enEditor = useMessageEditor();
    const frEditor = useMessageEditor();
    const enBaseline = useRef('');
    const frBaseline = useRef('');

    useEffect(() => {
        if (!data) return;
        enEditor?.commands.setContent(data.en ?? '');
        frEditor?.commands.setContent(data.fr ?? '');
        enBaseline.current = enEditor?.getHTML() ?? '';
        frBaseline.current = frEditor?.getHTML() ?? '';
    }, [data, enEditor, frEditor]);

    const handleSave = () => {
        const enHtml = enEditor?.getHTML() ?? '';
        const frHtml = frEditor?.getHTML() ?? '';
        const enUnchangedDefault = !data?.enIsCustom && enHtml === enBaseline.current;
        const frUnchangedDefault = !data?.frIsCustom && frHtml === frBaseline.current;
        saveMutation.mutate({
            facilityId,
            messages: {
                en: enUnchangedDefault ? '' : enHtml,
                fr: frUnchangedDefault ? '' : frHtml,
            },
        });
    };

    const handleTestEmail = (language: string, editor: Editor | null) => {
        testMutation.mutate({ language, bodyHtml: editor?.getHTML() ?? '', facilityId });
    };

    if (isLoading) return <Loader />;

    return (
        <Paper shadow="sm" radius="md" p="md">
            <Stack>
                <Title order={3}>{t('invitationMessage.admin.title')}</Title>
                <Text size="sm" c="dimmed">
                    {t('invitationMessage.admin.description')}
                </Text>

                <MessageSectionHeader
                    label={t('invitationMessage.admin.english')}
                    isCustom={data?.enIsCustom ?? false}
                    onReset={() => resetMutation.mutate({ facilityId, language: 'en' })}
                    resetting={resetMutation.isPending}
                />
                <MessageEditor editor={enEditor} />
                <Group justify="flex-end">
                    <Button
                        variant="default"
                        onClick={() => handleTestEmail('en', enEditor)}
                        loading={testMutation.isPending}
                    >
                        {t('invitationMessage.admin.testEmailEn')}
                    </Button>
                </Group>
                <InvitationPdfSection
                    facilityId={facilityId}
                    language="en"
                    currentFiles={pdfs?.en ?? []}
                />

                <Divider my="sm" />

                <MessageSectionHeader
                    label={t('invitationMessage.admin.french')}
                    isCustom={data?.frIsCustom ?? false}
                    onReset={() => resetMutation.mutate({ facilityId, language: 'fr' })}
                    resetting={resetMutation.isPending}
                />
                <MessageEditor editor={frEditor} />
                <Group justify="flex-end">
                    <Button
                        variant="default"
                        onClick={() => handleTestEmail('fr', frEditor)}
                        loading={testMutation.isPending}
                    >
                        {t('invitationMessage.admin.testEmailFr')}
                    </Button>
                </Group>
                <InvitationPdfSection
                    facilityId={facilityId}
                    language="fr"
                    currentFiles={pdfs?.fr ?? []}
                />

                <Group justify="flex-end">
                    <Button onClick={handleSave} loading={saveMutation.isPending}>
                        {t('invitationMessage.admin.save')}
                    </Button>
                </Group>
            </Stack>
        </Paper>
    );
}
